import { useRef } from 'react';
import { TailSpin } from 'react-loader-spinner';
import FormatDropdown from '@/features/downloadMedia/mediaFormat/FormatDropdown';
import {
  EditorSection,
  ImageEditorElements,
  ImageSelectorMedia,
} from '@/services/editorService';
import { useLocalization } from '@/services/localizationService';
import caretLeftGray from '@/public/images/icons/caret-left-gray.svg';
import useDownloadTab from '@/features/downloadMedia/downloadSidebar/individualDownload/useDownloadTab';
import useDownload from '@/features/downloadMedia/downloadSidebar/useDownload';
import equal from 'fast-deep-equal';

export default function DownloadTab({
  media,
  containsOnlyMediaInfo,
  onBackButtonClicked,
}: {
  media: ImageSelectorMedia;
  containsOnlyMediaInfo: boolean;
  onBackButtonClicked: () => void;
}) {
  const {
    download,
    needsExportBeforeDownload,
    onExportMedia,
    reprocessIfNeeded,
  } = useDownload();

  const {
    aiToolsChoices,
    aiToolbarElements,
    downloadButtonRef,
    isLoading,
    mediaNames,
    mediaPositionInRemoteResponse,
    onDownloadClick,
    onFormatSelection,
    onInputChange,
    taskToFormatAssociation,
  } = useDownloadTab({
    download,
    media,
    needsExportBeforeDownload,
    onExportMedia,
    reprocessIfNeeded,
  });

  const { t, data } = useLocalization();

  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      {containsOnlyMediaInfo && (
        <div className="flex pb-1 justify-between">
          <button type="button" onClick={onBackButtonClicked}>
            <img
              {...caretLeftGray}
              className="rtl:scale-x-[-1]"
              alt="Back icon"
            />
          </button>
          <img
            className="rounded-lg w-11/12"
            src={media.outputUrl ? media.outputUrl : media.inputUrl}
            alt={media.id}
          />
        </div>
      )}
      {!containsOnlyMediaInfo && (
        <>
          <p className="R10 uppercase text-gray-400 font-bold tracking-wider">
            {t(data.result.fileName)}
          </p>
          <div className="mt-3 h-8 w-full bg-overlay-white-10 border-none rounded-lg">
            <input
              value={mediaNames[mediaPositionInRemoteResponse]}
              ref={inputRef}
              className="SB12 bg-transparent outline-none text-start text-white w-full h-full px-3"
              type="text"
              onChange={(event) =>
                onInputChange(mediaPositionInRemoteResponse, event)
              }
            />
          </div>
        </>
      )}
      <p className="R10 uppercase text-gray-400 font-bold tracking-wider mt-5">
        {t(data.result.fileType)}
      </p>
      <FormatDropdown
        downloadFormat={taskToFormatAssociation[media.id!]}
        shouldShowMultipleFormatsLabel={false}
        onFormatSelection={onFormatSelection}
      />
      <p className="R10 uppercase text-gray-400 font-bold tracking-wider mt-8 mb-1">
        {t(data.result.aiModelsApplied)}
      </p>
      {aiToolbarElements.map((section: EditorSection) => (
        <div
          key={section.key}
          className="pt-3 pb-1 border-b border-overlay-white-5 last-of-type:border-transparent"
        >
          {section.pipelineSteps
            .filter((p) => p.tool in aiToolsChoices)
            .map((pipelineStep) => {
              const modelValue = aiToolsChoices[pipelineStep.tool];
              const model = pipelineStep.models.find((model) =>
                equal(model.value, modelValue)
              );

              const labelToDisplay =
                pipelineStep.visualization === ImageEditorElements.Toggle
                  ? pipelineStep.title!
                  : model?.title;

              const isFilters =
                pipelineStep.visualization === ImageEditorElements.Filters;
              const isNewColorSection =
                section.key === 'color' &&
                pipelineStep.visualization !== ImageEditorElements.Toggle;

              const shouldShowSectionIcon = isFilters || isNewColorSection;

              return (
                <div
                  key={pipelineStep.tool}
                  className={`mb-2 flex items-center ${
                    shouldShowSectionIcon
                      ? ''
                      : 'border-s-2 border-overlay-white-10'
                  }`}
                >
                  {shouldShowSectionIcon ? (
                    <img
                      className="w-[26px] h-[26px]"
                      {...section.icon}
                      alt={`model-${pipelineStep.tool}`}
                    />
                  ) : (
                    <img
                      className="w-[26px] h-[26px] ms-1"
                      src={pipelineStep.iconPath!}
                      alt={`model-${pipelineStep.tool}`}
                    />
                  )}
                  <div className="ms-2">
                    <p className="M12 text-gray-300">{labelToDisplay}</p>
                  </div>
                </div>
              );
            })}
        </div>
      ))}
      {!containsOnlyMediaInfo && (
        <button
          type="button"
          ref={downloadButtonRef}
          disabled={isLoading}
          className="w-full h-9 btn btn--large btn--primary rounded-full border-0 mt-6"
          onClick={async () => {
            await onDownloadClick();
          }}
        >
          {isLoading ? (
            <div className="px-12">
              <TailSpin
                color="white"
                height="22"
                width="22"
                aria-label="loading"
              />
            </div>
          ) : (
            <span className="M14">{t(data.common.download)}</span>
          )}
        </button>
      )}
    </>
  );
}
